
import EventManager from '@brainscape/event-manager';
import React, { useState, useEffect, useRef } from 'react';
import StringHelper from '_utils/StringHelper';
import TypeHelper from '_utils/TypeHelper';

import { toClassStr } from '_utils/UiHelper';
import { useSyncedRef } from '_hooks/useSyncedRef';

import {
  DismissButton,
} from '_views/shared/IconButton';

import defaultPackIconLightBg from '_images/shared/ugc-clear-bg.svg';


const SUGGESTION_GROUPS = [
  {type: 'crt', title: 'Brainscape Certified Flashcards'},
  {type: 'ugc', title: 'User Created Flashcards'},
  {type: 'tag', title: 'Flashcards by Subject (Certified and user-created)'},
];

const NewSearchBar = ({
  addClasses,
  domain,
  hasCloseButton = false,
  isMobileViewportSize,
  query = '',
  sidebarMode = 'full',
  suggestions = {},
}) => {

  let _isMounted = false;

  /*
  ==================================================
   HOOKS
  ==================================================
  */

  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const activeSuggestionIndexRef = useSyncedRef(activeSuggestionIndex);

  const [animateSuggestions, setAnimateSuggestions] = useState(false);

  const [displayedSuggestions, setDisplayedSuggestions] = useState({});
  const displayedSuggestionsRef = useSyncedRef(displayedSuggestions);

  const [shouldRenderSuggestions, setShouldRenderSuggestions] = useState(false);

  const componentRef = useRef(null);
  const fadeOutTimeout = useRef(null);
  const inputRef = useRef(null);


    // on Mount
  useEffect(() => {
    _isMounted = true;
    clearTimeoutsAndIntervals();
    document.addEventListener('keydown', handleKeydown);

    // on Unmount
    return () => {
      clearTimeoutsAndIntervals();
      document.removeEventListener('keydown', handleKeydown);
      _isMounted = false;
    };

  }, []);

  useEffect(() => {
    if (!TypeHelper.isEmptyObject(suggestions)) {

      setDisplayedSuggestions(suggestions);
      setShouldRenderSuggestions(true);

      requestAnimationFrame(() => setAnimateSuggestions(true));
      startOutsideClickMonitor()
      setActiveSuggestionIndex(-1);

    } else {
      setAnimateSuggestions(false);
      performSuggestionsFadeOut();
    }
  }, [suggestions]);

  useEffect(() => {
    if (activeSuggestionIndex < 0) return;

    requestAnimationFrame(() => {
      const activeEl = document.querySelector('.suggestion-item.is-active');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    });
  }, [activeSuggestionIndex]);


  /*
  ==================================================
   EVENT HANDLERS
  ==================================================
  */

  const handleArrowDownKeydown = (e) => {
    e.preventDefault();

    const count = getSuggestionItemCount();
    if (count < 1) return;

    let nextIndex;

    if (activeSuggestionIndexRef.current === -1) {
      nextIndex = 0;
      inputRef.current?.blur();
    } else {
      nextIndex = Math.min(activeSuggestionIndexRef.current + 1, count - 1);
    }

    setActiveSuggestionIndex(nextIndex);
  };

  const handleArrowUpKeydown = (e) => {
    e.preventDefault();

    const count = getSuggestionItemCount();
    if (count < 1 || activeSuggestionIndexRef.current === -1) return;

    if (activeSuggestionIndexRef.current === 0) {
      setActiveSuggestionIndex(-1);
      scrollToSuggestionsListTop();
      inputRef.current?.focus();
    } else {
      const nextIndex = activeSuggestionIndexRef.current - 1;
      setActiveSuggestionIndex(nextIndex);
    }
  };

  const handleClearButtonClick = () => {
    triggerQueryChange('');
  };

  const handleCloseButtonClick = () => {
    triggerCloseRequest();
  }

  const handleEnterKeydown = (e) => {
    const index = activeSuggestionIndexRef.current;

    if (index >= 0) {
      const activeSuggestionItem = document.querySelector('.suggestion-item.is-active a');

      if (activeSuggestionItem) {
        activeSuggestionItem.click();
        return true;
      }
    }

    triggerSearchRequest(query);
  };

  const handleEscapeKeydown = () => {
    dismissSuggestionsList();
  };

  const handleInputFocus = () => {
    triggerGotFocus();

    if (activeSuggestionIndexRef.current !== -1) {
      setActiveSuggestionIndex(-1);
      scrollToSuggestionsListTop();
    }
  };

  const handleKeydown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        handleArrowDownKeydown(e);
        break;
      case 'ArrowUp':
        handleArrowUpKeydown(e);
        break;
      case 'Enter':
        handleEnterKeydown(e);
        break;
      case 'Escape':
        handleEscapeKeydown();
        break;
      default:
        return false;
    }
  };

  const handleQueryChange = (e) => {
    const queryText = e.target.value;
    triggerQueryChange(queryText);
  };

  const handleOutsideClick = (e) => {
    e.stopPropagation();

    if (componentRef.current && componentRef.current.contains(e.target)) {
      return false;
    }

    dismissSuggestionsList();
  }


  /*
  ==================================================
   EVENT TRIGGERS
  ==================================================
  */

  const triggerCloseRequest = () => {
    EventManager.emitEvent('search-bar:close-request', {});
  };

  const triggerGotFocus = () => {
    EventManager.emitEvent('search-bar:got-focus', {});
  };

  const triggerQueryChange = (queryText) => {
    EventManager.emitEvent('search-bar:query-change', { queryText });
  };

  const triggerSearchRequest = () => {
    EventManager.emitEvent('search-bar:search-request', {});
  };


  /*
  ==================================================
   LOCAL UTILS
  ==================================================
  */

  const clearTimeoutsAndIntervals = () => {
    clearTimeout(fadeOutTimeout.current);
  }

  const dismissSuggestionsList = () => {
    performSuggestionsFadeOut();
  };

  const getSuggestionItemCount = () => {
    let count = 0;
    const suggestions = displayedSuggestionsRef.current;

    SUGGESTION_GROUPS.forEach(group => {
      count += suggestions[group.type]?.length || 0;
    });

    return count;
  };

  const performSuggestionsFadeOut = () => {
    setAnimateSuggestions(false);

    clearTimeout(fadeOutTimeout.current);

    fadeOutTimeout.current = setTimeout(() => {
      setShouldRenderSuggestions(false);
      setDisplayedSuggestions({});
      setActiveSuggestionIndex(-1);
      stopOutsideClickMonitor();

      clearTimeout(fadeOutTimeout.current);
    }, 300); // match CSS transition duration
  };

  const scrollToSuggestionsListTop = () => {
    const suggestionsContainer = document.querySelector('.suggestions');

    if (suggestionsContainer) {
      suggestionsContainer.scrollTop = 0;
    }
  };

  const startOutsideClickMonitor = () => {
    document.removeEventListener('click', handleOutsideClick);
    document.addEventListener('click', handleOutsideClick);
  }

  const stopOutsideClickMonitor = () => {
    document.removeEventListener('click', handleOutsideClick);
  }


  /*
  ==================================================
   SUB RENDERERS
  ==================================================
  */

  const renderClearQueryTextButton = () => {
    if (!query?.length > 0) return null;

    return (
      <DismissButton
        addClasses="clear-query-text-button"
        onClick={handleClearButtonClick}
      />
    );
  };

  const renderCloseButton = () => {
    if (!hasCloseButton) return null;

    return (
      <DismissButton
        addClasses="close-button"
        onClick={handleCloseButtonClick}
      />
    );
  }

  const renderSuggestions = () => {
    if (getSuggestionItemCount() < 0) {
      return null;
    }

    const animationClass = animateSuggestions
      ? 'is-visible'
      : shouldRenderSuggestions
        ? 'is-hidden'
        : 'is-removed';

    const classes = toClassStr(['suggestions', animationClass]);
    let groupOffsetIndex = 0;
    let suggestionGroups = [];

    SUGGESTION_GROUPS.forEach(group => {
      suggestionGroups.push(renderSuggestionGroup(group, groupOffsetIndex));
      const groupItemCount = displayedSuggestions[group.type]?.length || 0;
      groupOffsetIndex += groupItemCount;
    });

    return (
      <div className={classes}>
        {suggestionGroups}
      </div>
    );
  };

  const renderSuggestionGroup = (group, groupOffsetIndex) => {
    const type = group.type;
    const title = group.title;
    const suggestions = displayedSuggestions[type];

    if (!Array.isArray(suggestions) || suggestions.length < 1) {
      return null;
    }

    const suggestionGroupClass = `${type}-suggestion-group`;
    const classes = toClassStr(['suggestion-group', suggestionGroupClass]);
    let suggestionItemIndex = groupOffsetIndex;

    return (
      <div className={classes} key={`${type}-suggestion-group`}>
        <div className="suggestion-group-title">{title}</div>
        <ul className="suggestion-list">
          {suggestions.map(suggestion => renderSuggestionItem(suggestion, type, suggestionItemIndex++))}
        </ul>
      </div>
    );
  };

  const renderSuggestionItem = (suggestion, type, itemIndex) => {
    const suggestionItemClass = `${type}-suggestion-item`;
    const isActiveClass = itemIndex === activeSuggestionIndex ? 'is-active' : '';
    const classes = toClassStr(['suggestion-item', suggestionItemClass, isActiveClass]);
    const uniqueKey = `${itemIndex}-${StringHelper.convertCase(suggestion.path, 'kebab')}`;
    const linkText = StringHelper.highlightMatch(suggestion.name, query);

    return (
      <li key={uniqueKey} className={classes} id={uniqueKey}>
        <a className="suggestion-item-link" href={suggestion.path}>
          {renderSuggestionIcon(suggestion, type)}
          <span className="link-text" dangerouslySetInnerHTML={{ __html: linkText }} />
        </a>
      </li>
    );
  }

  const renderSuggestionIcon = (suggestion, type) => {
    if (type === 'tag') {
      return (
        <i className="suggestion-icon tag-suggestion-icon" />
      );
    }

    if (!suggestion.iconUrl) {
      return (
        <img className="suggestion-icon pack-suggestion-icon" src={defaultPackIconLightBg} alt={suggestion.name}/>
      );
    }

    return (
      <img className="suggestion-icon pack-suggestion-icon" src={suggestion.iconUrl} alt={suggestion.name}/>
    );
  }


  /*
  ==================================================
   EXPORTED COMPONENT
  ==================================================
  */

  const sidebarModeClass = sidebarMode === 'mini' ? 'is-mini-sidebar' : 'is-full-sidebar';
  const hasCloseButtonClass = hasCloseButton ? 'has-close-button' : '';
  const classes = toClassStr(['new-search-bar', sidebarModeClass, hasCloseButtonClass, addClasses]);

  return (
    <div className={classes} ref={componentRef}>

      <div className="query-box-and-suggestions">

        <div className="query-box">
          <div className="box-header">
            <i className="search-icon ion-ios-search-strong" />
          </div>

          <input
            autoFocus={true}
            className="search-input"
            onChange={handleQueryChange}
            onFocus={handleInputFocus}
            placeholder="Search..."
            ref={inputRef}
            type="text"
            value={query}
          />

          <div className="box-footer">
            {renderClearQueryTextButton()}
          </div>
        </div>

        {renderSuggestions()}
      </div>

      {renderCloseButton()}
    </div>
  );
};

export default NewSearchBar;
